import { createContext, useContext, useMemo, useState } from 'react';
import type { GroupedPromotion } from '@/app/components/hotel-detail/rooms/groupPromotionsByLabel';

/**
 * EvoModalsProvider - Centralized Modal State Management
 *
 * This provider serves as a centralized state manager for modals that can be triggered
 * from multiple places within the evo hotel detail page. The main benefits include:
 *
 * 1. **Avoiding Duplication**: Prevents rendering the same modal component multiple times
 *    across different components, which would lead to unnecessary DOM elements and potential
 *    performance issues.
 *
 * 2. **Single Source of Truth**: Ensures only one instance of each modal exists in the
 *    component tree, making state management predictable and consistent.
 *
 * 3. **Browser Navigation Integration**: Prevents conflicts when modals append their IDs
 *    to the URL for browser history support. Without centralization, multiple modal
 *    instances with the same ID could cause navigation issues where several modals
 *    might open simultaneously or interfere with each other.
 *
 * 4. **Clean URL Management**: Ensures that URL-based modal state management works
 *    correctly without conflicts from duplicate modal instances.
 */

type EvoModalsContextType = {
  // Interactive cancellation-type selection modal — choose a cancellation type before continuing to flights.
  isCancellationTypeSelectionModalOpen: boolean;
  openCancellationTypeSelectionModal: () => void;
  closeCancellationTypeSelectionModal: () => void;
  // Informational cancellation policies modal — per-room, so the triggering room is carried in state.
  // The modal shows the cancellation fees for every meal plan type in the room.
  cancellationPoliciesRoom: RoomType | undefined;
  isCancellationPoliciesModalOpen: boolean;
  openCancellationPoliciesModal: (room: RoomType) => void;
  closeCancellationPoliciesModal: () => void;
  // Informational room-promotion modal — shows a promotion group's label + all its descriptions.
  promotionInfo: GroupedPromotion | undefined;
  isPromotionInfoModalOpen: boolean;
  openPromotionInfoModal: (promotion: GroupedPromotion) => void;
  closePromotionInfoModal: () => void;
  // Informational meal plan modal — lists the meals and drinks the selected plan includes (MPH-946).
  // Held as the rate id, not the plan object: this provider outlives a rooms refetch, and a retained
  // config would let a forward navigation reopen the modal listing a rate that no longer exists.
  // EvoModals resolves the id against the current rooms on every render.
  mealPlanInfoRateId: string | undefined;
  isMealPlanInfoModalOpen: boolean;
  openMealPlanInfoModal: (mealPlanRateId: string) => void;
  closeMealPlanInfoModal: () => void;
};

const EvoModalsContext = createContext<EvoModalsContextType | undefined>(undefined);

export function EvoModalsProvider(props: { children: React.ReactNode }) {
  const [isCancellationTypeSelectionModalOpen, setCancellationTypeSelectionModalOpen] = useState<boolean>(false);
  const [isCancellationPoliciesModalOpen, setCancellationPoliciesModalOpen] = useState<boolean>(false);
  const [cancellationPoliciesRoom, setCancellationPoliciesRoom] = useState<RoomType | undefined>();
  const [isPromotionInfoModalOpen, setPromotionInfoModalOpen] = useState<boolean>(false);
  const [promotionInfo, setPromotionInfo] = useState<GroupedPromotion | undefined>();
  const [isMealPlanInfoModalOpen, setMealPlanInfoModalOpen] = useState<boolean>(false);
  const [mealPlanInfoRateId, setMealPlanInfoRateId] = useState<string | undefined>();

  const openCancellationTypeSelectionModal = () => setCancellationTypeSelectionModalOpen(true);
  const closeCancellationTypeSelectionModal = () => setCancellationTypeSelectionModalOpen(false);
  const openCancellationPoliciesModal = (room: RoomType) => {
    setCancellationPoliciesRoom(room);
    setCancellationPoliciesModalOpen(true);
  };
  const closeCancellationPoliciesModal = () => setCancellationPoliciesModalOpen(false);
  const openPromotionInfoModal = (promotion: GroupedPromotion) => {
    setPromotionInfo(promotion);
    setPromotionInfoModalOpen(true);
  };
  const closePromotionInfoModal = () => setPromotionInfoModalOpen(false);
  const openMealPlanInfoModal = (mealPlanRateId: string) => {
    setMealPlanInfoRateId(mealPlanRateId);
    setMealPlanInfoModalOpen(true);
  };
  const closeMealPlanInfoModal = () => setMealPlanInfoModalOpen(false);

  const modalsMemoized = useMemo(
    () => ({
      isCancellationTypeSelectionModalOpen,
      openCancellationTypeSelectionModal,
      closeCancellationTypeSelectionModal,
      cancellationPoliciesRoom,
      isCancellationPoliciesModalOpen,
      openCancellationPoliciesModal,
      closeCancellationPoliciesModal,
      promotionInfo,
      isPromotionInfoModalOpen,
      openPromotionInfoModal,
      closePromotionInfoModal,
      mealPlanInfoRateId,
      isMealPlanInfoModalOpen,
      openMealPlanInfoModal,
      closeMealPlanInfoModal,
    }),
    [
      isCancellationTypeSelectionModalOpen,
      isCancellationPoliciesModalOpen,
      cancellationPoliciesRoom,
      isPromotionInfoModalOpen,
      promotionInfo,
      isMealPlanInfoModalOpen,
      mealPlanInfoRateId,
    ]
  );

  return <EvoModalsContext.Provider value={modalsMemoized}>{props.children}</EvoModalsContext.Provider>;
}

/**
 * Hook to access the centralized modal state and actions.
 * Must be used within a EvoModalsProvider to ensure proper context access.
 */
export function useEvoModals() {
  const context = useContext(EvoModalsContext);

  if (context === undefined) {
    throw new Error('useEvoModals must be within a EvoModalsProvider');
  }

  return context;
}
